import React, { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";

const isoDate = (date) => date.toISOString().slice(0, 10);

function initialDates() {
  const today = new Date();
  const first = new Date(today);
  first.setDate(today.getDate() - 6);
  return { from: isoDate(first), to: isoDate(today) };
}

function Metric({ label, value, detail }) {
  return (
    <div className="nvl-card p-5" data-testid={`admin-stat-${label.toLowerCase().replaceAll(" ", "-")}`}>
      <p className="text-xs uppercase tracking-widest" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
        {label}
      </p>
      <p className="mt-2 text-3xl font-display">{value ?? "—"}</p>
      {detail ? <p className="mt-1 text-xs" style={{ color: "hsl(var(--nvl-ink-soft))" }}>{detail}</p> : null}
    </div>
  );
}

export default function AdminStatistics() {
  const dates = initialDates();
  const [fromDate, setFromDate] = useState(dates.from);
  const [toDate, setToDate] = useState(dates.to);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    if (!fromDate || !toDate) return;
    setLoading(true);
    setError("");
    try {
      const r = await api.get(`/admin/statistics?from_date=${fromDate}&to_date=${toDate}`);
      setData(r.data);
    } catch (e) {
      setData(null);
      setError(extractError(e, "Impossibile caricare le statistiche."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  const maxDaily = Math.max(1, ...(data?.daily_registrations || []).map((item) => item.count));

  return (
    <div className="max-w-5xl" data-testid="admin-statistics-page">
      <p className="nvl-overline" style={{ color: "hsl(var(--nvl-accent))" }}>Statistiche</p>
      <h1 className="mt-2 text-3xl md:text-4xl">Andamento di NOVAXLOVE</h1>
      <p className="mt-2 text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
        Gli utenti attivi sono quelli che hanno usato l’app nel periodo selezionato.
      </p>

      <div className="mt-6 nvl-card p-5 flex flex-wrap items-end gap-4">
        <label className="text-sm">
          <span className="block mb-2" style={{ color: "hsl(var(--nvl-ink-soft))" }}>Dal</span>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="nvl-input" data-testid="admin-statistics-from" />
        </label>
        <label className="text-sm">
          <span className="block mb-2" style={{ color: "hsl(var(--nvl-ink-soft))" }}>Al</span>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="nvl-input" data-testid="admin-statistics-to" />
        </label>
        <button type="button" onClick={load} disabled={loading || !fromDate || !toDate}
          className="nvl-btn-primary px-5 py-2.5 disabled:opacity-50" data-testid="admin-statistics-refresh">
          {loading ? "Aggiornamento…" : "Aggiorna"}
        </button>
      </div>

      {error ? <p className="mt-5 text-sm" style={{ color: "hsl(var(--destructive))" }}>{error}</p> : null}
      {!loading && data ? <>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Metric label="Utenti attivi" value={data.active_users} detail="nel periodo selezionato" />
          <Metric label="Nuovi iscritti" value={data.new_registrations} detail="nel periodo selezionato" />
          <Metric label="Account attivi" value={data.total_active_accounts} detail="totale attuale" />
          <Metric label="Email verificate" value={data.verified_active_accounts} detail="sugli account attivi" />
          <Metric label="Abbonamenti paganti" value={data.paid_memberships} detail="attivi ora" />
          <Metric label="Accessi omaggio" value={data.courtesy_access} detail="attivi ora" />
        </div>

        <section className="mt-6 nvl-card p-6" data-testid="admin-statistics-daily">
          <h2 className="text-xl font-display">Nuove iscrizioni giorno per giorno</h2>
          <div className="mt-5 space-y-3">
            {data.daily_registrations.map((item) => (
              <div key={item.date} className="grid grid-cols-[92px_1fr_28px] gap-3 items-center text-sm">
                <span style={{ color: "hsl(var(--nvl-ink-soft))" }}>{item.date.split("-").reverse().join("/")}</span>
                <div className="h-3 rounded-full overflow-hidden" style={{ backgroundColor: "hsl(var(--nvl-border))" }}>
                  <div className="h-full rounded-full" style={{ width: `${(item.count / maxDaily) * 100}%`, backgroundColor: "hsl(var(--nvl-accent))" }} />
                </div>
                <span className="text-right">{item.count}</span>
              </div>
            ))}
          </div>
        </section>
      </> : null}
    </div>
  );
}
